import React, { useRef, FC, useState, useEffect } from 'react';
import { Canvas, extend } from '@react-three/fiber';
import { Suspense } from 'react';
import * as THREE from 'three';
import FloatingPanel from '../FloatingPanel';
import Panel2D from '../Panel2D';
import { personalInfo } from '../../utils/data';
import { ErrorBoundary } from 'react-error-boundary';

// Import type declarations
import '../../types/three-jsx-elements.d.ts';

// Extend Three.js with custom elements
extend({ AmbientLight: THREE.AmbientLight, PointLight: THREE.PointLight });

// Type cast to avoid TypeScript errors
const AmbientLight = 'ambientLight' as any;
const PointLight = 'pointLight' as any;

const About: FC = () => {
  const aboutRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  // Monitor window size to detect mobile view
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  return (
    <section id="about" className="about-section" ref={aboutRef}>
      <Panel2D title="About Me">
        <div className="about-content">
          <h2 className="section-title">About Me</h2>
          
          <div className="about-container">
            <div className="about-panel">
              <div className="panel-header">
                <span className="panel-icon">⌘</span>
                <span className="panel-title">IDENTITY</span>
              </div>
              
              <div className="panel-content">
                <div className="about-text">
                  {personalInfo.about}
                </div>
                
                <div className="about-details">
                  <div className="detail-item">
                    <div className="detail-label">Location:</div>
                    <div className="detail-value">{personalInfo.location}</div>
                  </div>
                  
                  <div className="detail-item">
                    <div className="detail-label">Email:</div>
                    <div className="detail-value">
                      <a href={`mailto:${personalInfo.email}`}>{personalInfo.email}</a>
                    </div>
                  </div>
                  
                  <div className="detail-item">
                    <div className="detail-label">Academic Email:</div>
                    <div className="detail-value">
                      <a href={`mailto:${personalInfo.academicEmail}`}>{personalInfo.academicEmail}</a>
                    </div>
                  </div>
                  
                  <div className="detail-item">
                    <div className="detail-label">Website:</div>
                    <div className="detail-value">
                      <a href={personalInfo.social.website.url} target="_blank" rel="noopener noreferrer">
                        {personalInfo.social.website.name}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="about-3d">
              {isMobile ? (
                // Static alternative for mobile users - Removing the background box
                <div className="mobile-panel">
                  <div className="mobile-panel-content">
                    <h3 className="mobile-profile-header">PROFILE: AAYUSH KUMAR</h3>
                    <p>STATUS: ONLINE</p>
                    <p>MISSION: Building digital solutions to real-world problems</p>
                    <p>SPECIALIZATION: Full-stack development with AI/ML integration</p>
                  </div>
                </div>
              ) : (
                // 3D panel for desktop users
                <ErrorBoundary fallback={<div className="error-message">Failed to load 3D panel. Please refresh the page.</div>}>
                  <Suspense fallback={<div className="loading-message">Loading 3D content...</div>}>
                    <Canvas
                      shadows
                      camera={{ position: [0, 0, 5], fov: 50 }}
                      style={{ width: '100%', height: '100%' }}
                    >
                      <AmbientLight intensity={0.5} />
                      <PointLight position={[10, 10, 10]} intensity={0.8} />
                      
                      <FloatingPanel width={4} height={3}>
                        <div className="panel-3d-content">
                          <h3>PROFILE: AAYUSH KUMAR</h3>
                          <p>STATUS: ONLINE</p>
                          <p>MISSION: Building digital solutions to real-world problems</p>
                          <p>SPECIALIZATION: Full-stack development with AI/ML integration</p>
                        </div>
                      </FloatingPanel>
                    </Canvas>
                  </Suspense>
                </ErrorBoundary>
              )}
            </div>
          </div>
        </div>
      </Panel2D>
      
      <style>
        {`
        .about-section {
          min-height: 100vh;
          width: 100%;
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 80px 0;
        }
        
        .about-content {
          width: 100%;
          max-width: 1200px;
          padding: 0 20px;
        }
        
        .section-title {
          font-size: 42px;
          color: #00ff00;
          margin-bottom: 40px;
          text-align: center;
          text-shadow: 0 0 10px rgba(0, 255, 0, 0.7);
          font-family: 'VT323', monospace;
          letter-spacing: 3px;
        }
        
        .about-container {
          display: flex;
          justify-content: center;
          align-items: stretch;
          gap: 30px;
          flex-wrap: wrap;
        }
        
        .about-panel {
          flex: 1;
          min-width: 300px;
          background-color: rgba(0, 0, 0, 0.7);
          border: 1px solid #00ff00;
          border-radius: 5px;
          overflow: hidden;
          box-shadow: 0 0 15px rgba(0, 255, 0, 0.5);
        }
        
        .panel-header {
          background-color: rgba(0, 50, 0, 0.5);
          padding: 15px;
          display: flex;
          align-items: center;
          gap: 10px;
          border-bottom: 1px solid #00ff00;
        }
        
        .panel-icon {
          font-size: 20px;
          color: #00ff00;
        }
        
        .panel-title {
          font-family: 'VT323', monospace;
          font-size: 22px;
          letter-spacing: 2px;
          color: #00ff00;
        }
        
        .panel-content {
          padding: 20px;
        }
        
        .about-text {
          color: #00ff00;
          font-size: 16px;
          line-height: 1.6;
          margin-bottom: 30px;
        }
        
        .about-details {
          display: flex;
          flex-direction: column;
          gap: 15px;
        }
        
        .detail-item {
          display: flex;
          flex-direction: column;
          gap: 5px;
        }
        
        .detail-label {
          color: #00ff00;
          font-size: 14px;
          opacity: 0.8;
        }
        
        .detail-value {
          color: #00ff00;
          font-size: 16px;
        }
        
        .detail-value a {
          color: #00ff00;
          text-decoration: none;
          transition: all 0.2s;
        }
        
        .detail-value a:hover {
          text-shadow: 0 0 5px #00ff00;
        }
        
        .about-3d {
          flex: 1;
          min-width: 300px;
          height: 400px;
        }
        
        .panel-3d-content {
          font-family: 'Courier Prime', monospace;
          color: #00ff00;
          width: 100%;
          height: 100%;
          padding: 10px;
        }
        
        .panel-3d-content h3 {
          margin-bottom: 20px;
          font-weight: bold;
        }
        
        .panel-3d-content p {
          margin-bottom: 10px;
        }
        
        /* Mobile panel style (fallback for 3D content) */
        .mobile-panel {
          height: 100%;
          display: flex;
          flex-direction: column;
        }
        
        .mobile-panel-content {
          padding: 20px;
          display: flex;
          flex-direction: column;
          gap: 15px;
          color: #00ff00;
          font-family: 'Courier Prime', monospace;
        }
        
        .mobile-profile-header {
          font-family: 'VT323', monospace;
          font-size: 20px;
          color: #00ff00;
          text-align: center;
        }
        
        @media (max-width: 768px) {
          .about-container {
            flex-direction: column;
          }
          
          .about-3d {
            height: auto;
            min-height: 200px;
            width: 100%;
            max-width: 100%;
          }
          
          .section-title {
            font-size: 32px;
            margin-bottom: 30px;
          }
        }
        `}
      </style>
    </section>
  );
};

export default About;